import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { authApi, formatApiError } from "../../lib/authApi";
import { AuthShell, Field, PrimaryBtn, SmallLink, Notice } from "../../components/auth/AuthShell";

const ForgotPasswordPage = () => {
  const loc = useLocation();
  const [email, setEmail] = useState(loc.state?.email || "");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const t = setInterval(() => setRetryAfter((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [retryAfter]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const { data } = await authApi.forgotPassword(email);
      if (data?.throttled) {
        // Honest cooldown — an email was NOT sent. Show the wait.
        setRetryAfter(data.retry_after || 60);
      } else {
        setDone(true);
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Request failed.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <AuthShell title="Check your inbox"
                 lead="If an Archive account exists for that email, a reset link is on the way. It expires in 1 hour."
                 footer={<SmallLink to="/auth/login" data-testid="forgot-back-login">BACK TO SIGN IN</SmallLink>}>
        <Notice kind="success" data-testid="forgot-success">Reset link dispatched if the account exists.</Notice>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      lead="Enter the email tied to your Archive account. We'll send a single-use link to reset your password."
      footer={<><SmallLink to="/auth/login" data-testid="forgot-back-login">BACK TO SIGN IN</SmallLink></>}
    >
      {err && <Notice kind="error" data-testid="forgot-error">{err}</Notice>}
      {retryAfter > 0 && (
        <Notice kind="info" data-testid="forgot-cooldown">
          A reset link was requested for this address moments ago. You can
          request another in {retryAfter}s.
        </Notice>
      )}
      <form onSubmit={onSubmit} data-testid="forgot-form">
        <Field label="EMAIL" type="email" value={email} onChange={setEmail}
               required autoFocus autoComplete="email"
               data-testid="forgot-email" name="email" />
        <PrimaryBtn disabled={busy || retryAfter > 0} data-testid="forgot-submit">
          {busy ? "SENDING…" : retryAfter > 0 ? `WAIT ${retryAfter}S` : "SEND RESET LINK"}
        </PrimaryBtn>
      </form>
    </AuthShell>
  );
};

export default ForgotPasswordPage;
